import React, { ChangeEvent, FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IBaseComponent } from '../../../sharedInterfaces/IBaseComponent';
import { getDefaultLabelName } from '../../../services/DefaultLabelNameService';
import { ISliderValues } from '~/sharedInterfaces/SharedInterfaces';
import { IState } from '~/sharedInterfaces/IState';
import ComponentError from '../../ComponentError/ComponentError';
import { pushToGTM } from '~/actions/gtmActions';
import { getInputClassNames, hashCode } from '~/utils';
import { logUserFirstInteraction } from '~/actions/widgetContextActions';
import Tooltip from '../Tooltip/Tooltip';

interface ISliderProps extends IBaseComponent {
  label?: string;
  value: string | number;
  sliderValues?: ISliderValues;
  analyticsEvent: string;
  formatValueAmount: string;
  formatValueUnit: string;
  handleValidation?: () => void;
  tooltip?: boolean;
}

const Slider: FC<ISliderProps> = ({
  componentName,
  label,
  value,
  sliderValues,
  formatValueAmount,
  formatValueUnit,
  onChange,
  handleValidation,
  error,
  analyticsEvent,
  tooltip,
}) => {
  const { locale, settings } = useSelector((state: IState) => state.widgetContext);
  const componentLabel = label || getDefaultLabelName(locale, componentName).label;
  const componentTooltipText = getDefaultLabelName(locale, componentName).tooltipText;

  const uniqueKey = hashCode(`${settings.id}${componentName}`);
  const dispatch = useDispatch();

  const sendAnalytics = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    dispatch(
      pushToGTM({
        event: `${analyticsEvent}-change`,
        [analyticsEvent]: event.currentTarget.value,
        [`input-type-${analyticsEvent}`]: 'slider',
      })
    );
    dispatch(
      pushToGTM({
        event: 'q_user_a_slider_select',
      })
    );
  };

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (onChange) onChange(event);

    dispatch(logUserFirstInteraction());
  };

  useEffect(() => {
    if (handleValidation) handleValidation();
  }, []);

  return (
    <div
      className={getInputClassNames({
        tooltip,
        error,
        componentName,
        additionalClassName: 'qred-slider',
      })}
    >
      {componentTooltipText && <Tooltip tooltip={tooltip} tooltipText={componentTooltipText} />}
      <div className="qred-group">
        <div className="qred-row">
          <label htmlFor={uniqueKey}>
            <span className="qred-first">{componentLabel}</span>
          </label>
          <input
            name={uniqueKey}
            type="range"
            max={sliderValues && sliderValues.max}
            min={sliderValues && sliderValues.min}
            step={sliderValues && sliderValues.step}
            id={uniqueKey}
            value={value}
            onChange={handleOnChange}
            onMouseUp={sendAnalytics}
          />
          <p className="qred-first">
            <span className="qred-first">{formatValueAmount}</span>
            <span className="qred-second">{formatValueUnit}</span>
          </p>
          {error && <ComponentError error={error} />}
        </div>
      </div>
    </div>
  );
};

export default Slider;
